import React = require('react');
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { IGetGroupDetailInfoResult, IGroupInfo } from '../../../ts/models/IGroupInfo';
import { ISpaceInfoTypeItemCount } from '../../state/ContentCountsState';
import { GroupSubspaceName, SpaceInfo } from '../../state/SpaceInfo';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { Subspace, TabbedSpace } from '../TabbedSpace';
import { GroupDetailsSubspace } from './GroupDetailsSubspace';
import { GroupSpaceHeader } from './GroupSpaceHeader';
import { GroupUsersSubspace } from './GroupUsersSubspace';

export interface GroupSpaceStateProps {
  readonly spaceInfo: SpaceInfo;
  readonly contentCounts: ISpaceInfoTypeItemCount<'group'>;
  readonly contentCountsUpToDate: boolean;
  readonly canSessionUserManageUsers: boolean;
}

export interface GroupSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
  readonly addSiteUsersToGroup?: (group: IGroupInfo) => void;
}

export interface GroupSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly reloadSpace: () => void;
}
export type GroupSpaceProps = GroupSpaceStateProps & GroupSpaceOwnProps & GroupSpaceDispatchProps;

export class GroupSpace extends React.PureComponent<GroupSpaceProps> {

  private memoizedGetSiteUserSubspaces = defaultMemoize(getGroupSubspaces);

  public componentDidUpdate(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public componentDidMount(): void {
    this.props.fetchUpdatedContentCounts();
  }

  public render() {
    const group = this.props.spaceInfo.item as IGroupInfo;
    const groupDetails = this.props.spaceInfo.item as IGetGroupDetailInfoResult;

    const subspaces = this.memoizedGetSiteUserSubspaces(
      groupDetails,
      this.props.contentCountsUpToDate,
      this.props.contentCounts,
      group,
      this.props.canSessionUserManageUsers,
      this.props.addSiteUsersToGroup
    );

    return (
      <TabbedSpace
        subspaces = {subspaces}
        activeSubspaceName = {this.props.spaceInfo.subspaceName}
        urlForSubspace = {this.getGroupSubspaceUrl}
        mastheadElement = {this.mastheadElement()}
        testIdPrefix = {'groupspace'}
        spaceItemName = {groupDetails.name}
      />
    );
  }

  private mastheadElement(): React.ReactNode {
    return <GroupSpaceHeader
      item={this.props.spaceInfo.item as IGroupInfo}
    />;
  }

  private static groupUrlBySubspace: Record<GroupSubspaceName, (urls: SpacesSiteUrls, groupId: string) => string> = {
    users: (urls, groupId) =>  urls.groupUsersUrl(groupId),
    details: (urls, groupId) =>  urls.groupDetailsUrl(groupId),
  };

  private getGroupSubspaceUrl = (subspaceName?: GroupSubspaceName) => {
    let url = this.props.urls.groupUrl(this.props.spaceInfo.item.id);
    if (subspaceName) {
      const urlFn = GroupSpace.groupUrlBySubspace[subspaceName];
      url = urlFn && urlFn(this.props.urls, this.props.spaceInfo.item.id) || url;
    }
    return url;
  }
}


function getGroupSubspaces(
  groupDetails: IGetGroupDetailInfoResult,
  contentCountsUpToDate: boolean,
  contentCounts: ISpaceInfoTypeItemCount<'group'>,
  group: IGroupInfo,
  canSessionUserManageUsers: boolean,
  addSiteUsersToGroup: (group: IGroupInfo) => void
): ReadonlyArray<Subspace> {
  return _.compact([
    {
      name: 'users',
      tabLabel: Messages.members(),
      shouldRenderTabContentCount: true,
      tabContentCount: contentCounts && contentCounts['users'],
      tabContentCountUpToDate: contentCountsUpToDate,
      renderContent: () => {
        return (<GroupUsersSubspace
                  group={group}
                  canSessionUserManageUsers={canSessionUserManageUsers}
                  addSiteUsersToGroup={addSiteUsersToGroup} />);
      }
    },
    {
      name: 'details',
      tabLabel: Messages.details(),
      shouldRenderTabContentCount: false,
      renderContent: () => {
        return (
          <GroupDetailsSubspace
            group={groupDetails}
          />
        );
      }
    }
  ]);
}
